import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { getUnixTime } from '@einhaus/meteoric-helpers';
import { trpc } from '@/api/trpc';

const money = (value: number | string) => Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const eastern = (value: number) => new Date(value * 1000).toLocaleString('en-US', { timeZone: 'America/New_York' });
const inputClass = 'w-full rounded border border-slate-600 bg-slate-900 p-2';

export default function FuturesPaperStrategyAdmission({ contracts }: { contracts: { id: number; canonicalSymbol: string }[] }) {
    const [search] = useSearchParams();
    const [form, setForm] = useState({
        caseId: search.get('caseId') ?? '',
        contractId: '',
        entryLimit: '',
        closeLimit: '',
        start: '',
        end: ''
    });
    const [selected, setSelected] = useState(0);
    const [error, setError] = useState('');
    const [working, setWorking] = useState(false);
    const [ack, setAck] = useState(false);
    const [enableAck, setEnableAck] = useState(false);
    const preview = useMutation(trpc.admin.previewFuturesPaperStrategyAdmission.mutationOptions({ retry: false }));
    const create = useMutation(trpc.admin.createFuturesPaperStrategyAdmission.mutationOptions({ retry: false }));
    const reviewArm = useMutation(trpc.admin.reviewFuturesPaperStrategyAdmission.mutationOptions({ retry: false }));
    const arm = useMutation(trpc.admin.armFuturesPaperStrategyAdmission.mutationOptions({ retry: false }));
    const enable = useMutation(trpc.admin.enableFuturesPaperAdmissionPortfolio.mutationOptions({ retry: false }));
    const stop = useMutation(trpc.admin.stopFuturesPaperStrategyAdmission.mutationOptions({ retry: false }));
    const list = useQuery(trpc.admin.listFuturesPaperStrategyAdmissions.queryOptions());
    const status = useQuery({
        ...trpc.admin.getFuturesPaperStrategyAdmission.queryOptions({ enrollmentId: selected }),
        enabled: selected > 0,
        refetchInterval: selected ? 5000 : false
    });
    const [draft, setDraft] = useState<{
        request: Parameters<typeof preview.mutateAsync>[0];
        result: Awaited<ReturnType<typeof preview.mutateAsync>>;
    } | null>(null);
    const row = status.data?.admission;
    const resetReview = () => {
        setDraft(null);
        reviewArm.reset();
        setAck(false);
        setEnableAck(false);
        setError('');
    };
    async function act(work: () => Promise<unknown>) {
        setWorking(true);
        setError('');
        try {
            await work();
        } catch (failure) {
            setError(
                `${failure instanceof Error ? failure.message : 'Request failed'}. Refresh the saved enrollment before any further action; do not retry an unknown submission.`
            );
        } finally {
            await list.refetch();
            if (selected) await status.refetch();
            setWorking(false);
        }
    }
    return (
        <section id="strategy-admission" className="space-y-4 rounded border border-slate-700 p-5">
            <h2 className="text-xl font-semibold">Reviewed strategy → MES paper</h2>
            <p>
                Use a passing native portfolio proposal, one fixed five-minute exit and its original one-minute SPY strategy. One long MES
                contract, one attempt, $5,000 allocation and a $3,500 margin-plus-entry-commission ceiling.
            </p>
            <p>
                Saving creates an inactive enrollment. Arming authorizes one SIM attempt but leaves the source strategy and portfolio
                activation unchanged. Enable the reviewed paper portfolio and activate its strategy separately only after approval.
            </p>
            {(error || list.error || status.error) && (
                <p role="alert" className="text-red-300">
                    {error || list.error?.message || status.error?.message}
                </p>
            )}
            <form
                className="grid gap-3 sm:grid-cols-2"
                onSubmit={(event) => {
                    event.preventDefault();
                    resetReview();
                    void act(async () => {
                        const request = {
                            caseId: Number(form.caseId),
                            contractId: Number(form.contractId),
                            entryLimit: Number(form.entryLimit),
                            closeLimit: Number(form.closeLimit),
                            startsTimestamp: getUnixTime(form.start.replace('T', ' ')),
                            endsTimestamp: getUnixTime(form.end.replace('T', ' '))
                        };
                        setDraft({ request, result: await preview.mutateAsync(request) });
                    });
                }}
            >
                <label>
                    Reviewed portfolio proposal ID
                    <input
                        className={inputClass}
                        type="number"
                        min="1"
                        required
                        value={form.caseId}
                        onChange={(event) => {
                            resetReview();
                            setForm({ ...form, caseId: event.target.value });
                        }}
                    />
                </label>
                <label>
                    Dated MES contract
                    <select
                        className={inputClass}
                        required
                        value={form.contractId}
                        onChange={(event) => {
                            resetReview();
                            setForm({ ...form, contractId: event.target.value });
                        }}
                    >
                        <option value="">Choose exact contract</option>
                        {contracts.map((contract) => (
                            <option key={contract.id} value={contract.id}>
                                {contract.canonicalSymbol}
                            </option>
                        ))}
                    </select>
                </label>
                {(['entryLimit', 'closeLimit'] as const).map((key) => (
                    <label key={key}>
                        {key === 'entryLimit' ? 'Entry buy cap' : 'Protective sell floor'}
                        <input
                            className={inputClass}
                            type="number"
                            min="0.25"
                            step="0.25"
                            required
                            value={form[key]}
                            onChange={(event) => {
                                resetReview();
                                setForm({ ...form, [key]: event.target.value });
                            }}
                        />
                    </label>
                ))}
                {(['start', 'end'] as const).map((key) => (
                    <label key={key}>
                        {key === 'start' ? 'Entry window starts' : 'Entry window ends'} (Eastern)
                        <input
                            className={inputClass}
                            type="datetime-local"
                            step="60"
                            required
                            value={form[key]}
                            onChange={(event) => {
                                resetReview();
                                setForm({ ...form, [key]: event.target.value });
                            }}
                        />
                    </label>
                ))}
                <button className="rounded bg-sky-800 p-2 disabled:opacity-40" disabled={working}>
                    Preview inactive enrollment
                </button>
            </form>
            {draft && (
                <div className="space-y-2 rounded border border-slate-600 p-3">
                    <p>
                        Proposal {draft.result.caseId} · strategy {draft.result.strategyId} · {draft.result.brokerSymbol} · one contract.
                    </p>
                    <p>
                        {eastern(draft.result.startsTimestamp)}–{eastern(draft.result.endsTimestamp)} Eastern; at most five minutes holding.
                        Buy cap {draft.result.entryLimit}; sell floor {draft.result.closeLimit}. Existing cash{' '}
                        {money(draft.result.cashBalance)}.
                    </p>
                    <button
                        className="rounded bg-sky-800 p-2 disabled:opacity-40"
                        disabled={working}
                        onClick={() => {
                            const reviewed = draft;
                            setDraft(null);
                            void act(async () => {
                                const saved = await create.mutateAsync({
                                    ...reviewed.request,
                                    expectedConfigurationKey: reviewed.result.configurationKey
                                });
                                setSelected(saved.admission.id);
                            });
                        }}
                    >
                        Save inactive enrollment
                    </button>
                </div>
            )}
            <label className="block">
                Saved enrollments
                <select
                    className={inputClass}
                    value={selected}
                    onChange={(event) => {
                        resetReview();
                        setSelected(Number(event.target.value));
                    }}
                >
                    <option value="0">Choose enrollment</option>
                    {list.data?.map((item) => (
                        <option key={item.id} value={item.id}>
                            #{item.id} · strategy {item.strategyId} · {item.status}
                        </option>
                    ))}
                </select>
            </label>
            {row && (
                <div className="space-y-3">
                    <p>
                        Enrollment #{row.id}: {row.status}. Source strategy {status.data?.strategyActive ? 'active' : 'inactive'}; paper
                        portfolio {status.data?.portfolioEnabled ? 'enabled' : 'inactive'}. {row.lastError}
                    </p>
                    <p>
                        Proposal #{row.caseId}; strategy #{row.strategyId}; portfolio #{row.portfolioId}. Limits {row.entryLimit} /{' '}
                        {row.closeLimit}. Entry {eastern(row.startsTimestamp)}–{eastern(row.endsTimestamp)} Eastern.
                    </p>
                    {row.status === 'draft' && (
                        <button
                            className="rounded bg-sky-800 p-2 disabled:opacity-40"
                            disabled={working}
                            onClick={() => {
                                reviewArm.reset();
                                setAck(false);
                                void act(() => reviewArm.mutateAsync({ enrollmentId: row.id }));
                            }}
                        >
                            Review fresh arming checks
                        </button>
                    )}
                    {reviewArm.data && row.status === 'draft' && (
                        <div className="space-y-2 rounded border border-amber-700 p-3">
                            <p>
                                {reviewArm.data.preview.brokerSymbol}: trade {reviewArm.data.preview.lastTradePrice} at{' '}
                                {eastern(reviewArm.data.preview.sourceTradeTimestamp)} Eastern. Native margin{' '}
                                {money(reviewArm.data.preview.initialMargin)}; estimated entry commission{' '}
                                {money(reviewArm.data.preview.estimatedCommission)}. Buy cap {row.entryLimit}; protective floor{' '}
                                {row.closeLimit}. Deadline protection uses a SIM market close without a price floor, even if the quote feed
                                fails. A halt can delay a fill; the native owner stays reserved until broker-flat reconciliation.
                            </p>
                            <label className="block">
                                <input type="checkbox" checked={ack} onChange={(event) => setAck(event.target.checked)} /> I reviewed these
                                limits and authorize one bounded MES SIM attempt.
                            </label>
                            <button
                                className="rounded bg-amber-800 p-2 disabled:opacity-40"
                                disabled={working || !ack}
                                onClick={() => {
                                    const reviewed = reviewArm.data;
                                    if (!reviewed) return;
                                    reviewArm.reset();
                                    setAck(false);
                                    void act(() =>
                                        arm.mutateAsync({
                                            enrollmentId: row.id,
                                            expectedConfigurationKey: reviewed.admission.configurationKey,
                                            expectedInstrumentKey: reviewed.preview.instrumentKey,
                                            acknowledgeOneNativePaperAttempt: true
                                        })
                                    );
                                }}
                            >
                                Arm exactly this enrollment
                            </button>
                        </div>
                    )}
                    {row.status === 'armed' && !status.data?.portfolioEnabled && (
                        <div className="space-y-2 rounded border border-amber-700 p-3">
                            <label>
                                <input type="checkbox" checked={enableAck} onChange={(event) => setEnableAck(event.target.checked)} />{' '}
                                Enable only this reviewed paper portfolio; its armed source may trade during the frozen window.
                            </label>
                            <button
                                className="rounded bg-amber-800 p-2 disabled:opacity-40"
                                disabled={working || !enableAck}
                                onClick={() => {
                                    setEnableAck(false);
                                    void act(() =>
                                        enable.mutateAsync({
                                            enrollmentId: row.id,
                                            expectedConfigurationKey: row.configurationKey,
                                            acknowledgeEnablePaperPortfolio: true
                                        })
                                    );
                                }}
                            >
                                Enable reviewed paper portfolio
                            </button>
                        </div>
                    )}
                    {['draft', 'armed'].includes(row.status) && (
                        <button
                            className="rounded border border-slate-500 p-2 disabled:opacity-40"
                            disabled={working}
                            onClick={() => {
                                resetReview();
                                void act(() => stop.mutateAsync({ enrollmentId: row.id }));
                            }}
                        >
                            Stop unused enrollment
                        </button>
                    )}
                    {row.status === 'submitted' && (
                        <p>
                            The native owner retains this attempt through fill reconciliation and protective close. Do not submit a
                            replacement.
                        </p>
                    )}
                    {['completed', 'expired', 'stopped'].includes(row.status) && (
                        <p>
                            This enrollment cannot submit again. Review the original outcome and turn its strategy off through the normal
                            strategy controls.
                        </p>
                    )}
                    {row.positionId != null && (
                        <a className="underline" href={`/tools/futuresPaper?positionId=${row.positionId}`}>
                            Inspect original native position #{row.positionId}
                        </a>
                    )}
                    <button className="ml-3 underline" disabled={working} onClick={() => void status.refetch()}>
                        Refresh saved status
                    </button>
                </div>
            )}
        </section>
    );
}
